import * as z from 'zod/mini'

/**
 * Kontaktný formulár: pravidlá a volanie API (kontrakt: docs/product/2026-10-viditelnost-a-dopyty/spec.md, K1 až K5).
 * Modul nezávisí od Reactu ani od jazyka stránky, takže ho znova použije aj ostrov v Astre.
 */

/** Najväčšia dĺžka správy. Server má rovnaký limit (K5); obe strany merajú jednotky UTF-16 (`length`). */
export const MESSAGE_MAX = 5000

/**
 * Dĺžka v jednotkách UTF-16 (`length`) ako na serveri (Java `@Size`, K5). Vstavané `z.minLength`/`z.maxLength`
 * v zod 4 počítajú znaky Unicode, takže by napríklad pri emoji pustili text, ktorý server odmietne.
 */
const minUnits = (min: number, message: string) => z.refine<string>((value) => value.length >= min, message)
const maxUnits = (max: number, message: string) => z.refine<string>((value) => value.length <= max, message)

/**
 * Hlášky sú kľúče i18n (`contact.v.*`), nie texty: preloží ich komponent v aktuálnom jazyku, takže sa po
 * prepnutí jazyka zmenia aj už zobrazené chyby. Prázdne pole (aj samé medzery) je „povinné“, nie „krátke“.
 * Kontroly bežia v uvedenom poradí, `trim` prvý. `zod/mini` namiesto plného `zod`: rovnaké jadro,
 * ale výrazne menší JS (rozpočet obsahového webu je 100 kB gzip).
 */
export const contactSchema = z.object({
  name: z.string().check(
    z.trim(),
    minUnits(1, 'contact.v.name_required'),
    minUnits(2, 'contact.v.name_min'),
    maxUnits(200, 'contact.v.name_max'),
  ),
  email: z.string().check(
    z.trim(),
    minUnits(1, 'contact.v.email_required'),
    maxUnits(200, 'contact.v.email_max'),
    z.email('contact.v.email_invalid'),
  ),
  company: z.string().check(z.trim(), maxUnits(200, 'contact.v.company_max')),
  message: z.string().check(
    z.trim(),
    minUnits(1, 'contact.v.message_required'),
    minUnits(10, 'contact.v.message_min'),
    maxUnits(MESSAGE_MAX, 'contact.v.message_max'),
  ),
})

export type ContactFormData = z.infer<typeof contactSchema>

export type ApiLang = 'sk' | 'cs' | 'en'

/** Skutočný jazyk rozhrania pre pole `lang` (AK1). Neznámy jazyk sa neposiela (server by ho aj tak zahodil). */
export function toApiLang(language: string | undefined): ApiLang | undefined {
  const code = language?.toLowerCase().split('-')[0]
  return code === 'sk' || code === 'cs' || code === 'en' ? code : undefined
}

/** Pri zaseknutom spojení formulár po čase ohlási chybu, aby neostal navždy v stave „Odosielam…“. Nginx dá 504 po 30 s. */
const REQUEST_TIMEOUT_MS = 35_000

/**
 * Odošle dopyt. Úspech = ľubovoľné 2xx; telo odpovede sa ignoruje (rozdiel `sent` / `stored` nie je pre
 * návštevníka dôležitý). Pri inom stave (400, 500, 503 z nginxu…) aj pri sieťovej chybe vráti `false`
 * a telo odpovede sa nečíta ani nezobrazuje: obsahuje len kódy, a text chyby patrí do i18n.
 */
export async function sendContact(data: ContactFormData, language: string | undefined): Promise<boolean> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
  try {
    const res = await fetch('/api/v1/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...data, lang: toApiLang(language) }),
      signal: controller.signal,
    })
    return res.ok
  } catch {
    return false
  } finally {
    clearTimeout(timer)
  }
}
